import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { ggosDeviceService } from '../../services/api';
import './FaceMeshViewer.css';

/* ═══════════════════════════════════════════════════════════════════
   PROCEDURAL FACE GEOMETRY
   A parametric face surface with anatomical features:
   nose protrusion, eye sockets, brow ridge, cheek curvature, chin.
   64×80 grid = 5,120 vertices → smooth shading.
   ═══════════════════════════════════════════════════════════════════ */
const FACE_GRID_W = 64, FACE_GRID_H = 80;

function createFaceGeometry() {
  const geo = new THREE.PlaneGeometry(0.26, 0.34, FACE_GRID_W, FACE_GRID_H);
  const pos = geo.attributes.position;

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);

    const nx = x / 0.13;
    const ny = y / 0.17;

    const r2 = nx * nx * 0.8 + ny * ny;
    let z = Math.sqrt(Math.max(0, 1.0 - r2)) * 0.06;

    // Nose
    const noseX = Math.abs(nx);
    const noseCenter = ny + 0.05;
    if (noseX < 0.18) {
      const bridgeProfile = Math.max(0, 1 - noseX / 0.18);
      const bridgeY = Math.max(0, 1 - Math.abs(noseCenter - 0.15) / 0.35);
      z += 0.035 * bridgeProfile * bridgeProfile * bridgeY;
    }
    const noseTipDist = Math.sqrt(nx * nx + (noseCenter + 0.1) * (noseCenter + 0.1));
    if (noseTipDist < 0.22) z += 0.025 * Math.max(0, 1 - noseTipDist / 0.22);
    const nostrilL = Math.sqrt((nx + 0.14) * (nx + 0.14) + (noseCenter + 0.05) * (noseCenter + 0.05));
    const nostrilR = Math.sqrt((nx - 0.14) * (nx - 0.14) + (noseCenter + 0.05) * (noseCenter + 0.05));
    if (nostrilL < 0.08) z -= 0.008 * (1 - nostrilL / 0.08);
    if (nostrilR < 0.08) z -= 0.008 * (1 - nostrilR / 0.08);

    // Eye sockets
    const eyeY = 0.22, eyeSpacing = 0.32, eyeW = 0.14, eyeH = 0.08;
    const eyeLDx = (nx + eyeSpacing) / eyeW, eyeLDy = (ny - eyeY) / eyeH;
    const eyeRDx = (nx - eyeSpacing) / eyeW, eyeRDy = (ny - eyeY) / eyeH;
    const eyeLD = eyeLDx * eyeLDx + eyeLDy * eyeLDy;
    const eyeRD = eyeRDx * eyeRDx + eyeRDy * eyeRDy;
    if (eyeLD < 1) z -= 0.012 * (1 - eyeLD) * (1 - eyeLD);
    if (eyeRD < 1) z -= 0.012 * (1 - eyeRD) * (1 - eyeRD);

    // Brow ridge
    const browY = ny - 0.38;
    if (Math.abs(browY) < 0.08 && Math.abs(nx) < 0.65) {
      z += 0.008 * Math.max(0, 1 - Math.abs(browY) / 0.08) * Math.max(0, 1 - Math.abs(nx) / 0.65);
    }

    // Cheekbones
    const cheekLD = Math.sqrt((nx + 0.55) * (nx + 0.55) + (ny - 0.05) * (ny - 0.05));
    const cheekRD = Math.sqrt((nx - 0.55) * (nx - 0.55) + (ny - 0.05) * (ny - 0.05));
    if (cheekLD < 0.3) z += 0.006 * (1 - cheekLD / 0.3);
    if (cheekRD < 0.3) z += 0.006 * (1 - cheekRD / 0.3);

    // Chin
    const chinDist = Math.sqrt(nx * nx + (ny + 0.85) * (ny + 0.85));
    if (chinDist < 0.25) z += 0.015 * Math.max(0, 1 - chinDist / 0.25);

    // Lips area
    const lipY = ny + 0.35;
    if (Math.abs(lipY) < 0.1 && Math.abs(nx) < 0.25) {
      z += 0.008 * Math.max(0, 1 - Math.abs(lipY) / 0.1) * Math.max(0, 1 - Math.abs(nx) / 0.25);
    }

    // Fade edges to zero
    z *= Math.max(0, 1 - r2 * 0.9);

    pos.setZ(i, z);
  }
  geo.computeVertexNormals();
  return geo;
}

/* ─── Blendshape deformation for procedural face ─────────────── */
const PROC_BLENDSHAPES = {
  eyeBlinkLeft:   [{ cx: -0.32, cy: 0.22, r: 0.12, dy: -0.003 }],
  eyeBlinkRight:  [{ cx: 0.32, cy: 0.22, r: 0.12, dy: -0.003 }],
  eyeWideLeft:    [{ cx: -0.32, cy: 0.22, r: 0.15, dy: 0.003 }],
  eyeWideRight:   [{ cx: 0.32, cy: 0.22, r: 0.15, dy: 0.003 }],
  browInnerUp:    [{ cx: 0, cy: 0.45, r: 0.25, dy: 0.004 }],
  browDownLeft:   [{ cx: -0.3, cy: 0.4, r: 0.15, dy: -0.004 }],
  browDownRight:  [{ cx: 0.3, cy: 0.4, r: 0.15, dy: -0.004 }],
  browOuterUpLeft: [{ cx: -0.5, cy: 0.4, r: 0.15, dy: 0.004 }],
  browOuterUpRight:[{ cx: 0.5, cy: 0.4, r: 0.15, dy: 0.004 }],
  jawOpen:        [{ cx: 0, cy: -0.7, r: 0.35, dy: -0.01 }],
  mouthSmileLeft: [{ cx: -0.22, cy: -0.35, r: 0.12, dx: -0.003, dy: 0.003 }],
  mouthSmileRight:[{ cx: 0.22, cy: -0.35, r: 0.12, dx: 0.003, dy: 0.003 }],
  mouthFrownLeft: [{ cx: -0.22, cy: -0.35, r: 0.12, dy: -0.003 }],
  mouthFrownRight:[{ cx: 0.22, cy: -0.35, r: 0.12, dy: -0.003 }],
  mouthPucker:    [{ cx: 0, cy: -0.35, r: 0.15, dz: 0.006 }],
  cheekPuff:      [{ cx: -0.5, cy: -0.05, r: 0.2, dz: 0.005, dx: -0.003 },
                   { cx: 0.5, cy: -0.05, r: 0.2, dz: 0.005, dx: 0.003 }],
  noseSneerLeft:  [{ cx: -0.15, cy: 0.05, r: 0.1, dy: 0.002, dz: 0.002 }],
  noseSneerRight: [{ cx: 0.15, cy: 0.05, r: 0.1, dy: 0.002, dz: 0.002 }],
};

function applyProcBlendshapes(geo, blendshapes) {
  if (!blendshapes) return;
  const pos = geo.attributes.position;
  const basePos = geo.userData.basePositions;
  if (!basePos) return;
  for (let i = 0; i < pos.count; i++) {
    pos.setX(i, basePos[i * 3]);
    pos.setY(i, basePos[i * 3 + 1]);
    pos.setZ(i, basePos[i * 3 + 2]);
  }
  for (const [bsName, regions] of Object.entries(PROC_BLENDSHAPES)) {
    const val = blendshapes[bsName];
    if (!val || val < 0.01) continue;
    for (const reg of regions) {
      for (let i = 0; i < pos.count; i++) {
        const bx = pos.getX(i) / 0.13;
        const by = pos.getY(i) / 0.17;
        const dist = Math.sqrt((bx - reg.cx) ** 2 + (by - reg.cy) ** 2);
        if (dist >= reg.r) continue;
        const weight = (1 - dist / reg.r) * val;
        if (reg.dx) pos.setX(i, pos.getX(i) + reg.dx * weight);
        if (reg.dy) pos.setY(i, pos.getY(i) + reg.dy * weight);
        if (reg.dz) pos.setZ(i, pos.getZ(i) + reg.dz * weight);
      }
    }
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
}

/* ─── Key blendshapes for HUD ────────────────────────────────── */
const KEY_BLENDSHAPES = [
  { key: 'mouthSmileLeft', label: 'Smile L', emoji: '\u{1F60A}' },
  { key: 'mouthSmileRight', label: 'Smile R', emoji: '\u{1F60A}' },
  { key: 'eyeBlinkLeft', label: 'Blink L', emoji: '\u{1F611}' },
  { key: 'eyeBlinkRight', label: 'Blink R', emoji: '\u{1F611}' },
  { key: 'browInnerUp', label: 'Brow \u2191', emoji: '\u{1F928}' },
  { key: 'browDownLeft', label: 'Brow \u2193L', emoji: '\u{1F620}' },
  { key: 'browDownRight', label: 'Brow \u2193R', emoji: '\u{1F620}' },
  { key: 'jawOpen', label: 'Jaw', emoji: '\u{1F62E}' },
  { key: 'mouthFunnel', label: 'Funnel', emoji: '\u{1F617}' },
  { key: 'cheekPuff', label: 'Puff', emoji: '\u{1F421}' },
  { key: 'eyeSquintLeft', label: 'Squint L', emoji: '\u{1F606}' },
  { key: 'eyeSquintRight', label: 'Squint R', emoji: '\u{1F606}' },
  { key: 'noseSneerLeft', label: 'Sneer L', emoji: '\u{1F922}' },
  { key: 'noseSneerRight', label: 'Sneer R', emoji: '\u{1F922}' },
  { key: 'mouthPucker', label: 'Pucker', emoji: '\u{1F619}' },
  { key: 'mouthFrownLeft', label: 'Frown L', emoji: '\u2639\uFE0F' },
  { key: 'mouthFrownRight', label: 'Frown R', emoji: '\u2639\uFE0F' },
];

const YAW_ORDER = ['yaw_neg30','yaw_neg15','yaw_0','yaw_pos15','yaw_pos30'];
const YAW_LABELS = { yaw_neg30: '-30\u00B0', yaw_neg15: '-15\u00B0', yaw_0: '0\u00B0', yaw_pos15: '+15\u00B0', yaw_pos30: '+30\u00B0' };
const PLAYBACK_SPEEDS = [0.25, 0.5, 1, 2, 4];
const MAX_TIMELINE_FRAMES = 100;

function qualityTier(q) { if (q >= 0.75) return 'high'; if (q >= 0.50) return 'medium'; return 'low'; }
function formatTimestamp(ts) { if (!ts) return '\u2014'; return new Date(ts).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
function formatShortTime(ts) { if (!ts) return '\u2014'; return new Date(ts).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
function drowsinessLabel(level) { if (!level) return null; const map = { alert: ['Alert','\u{1F7E2}'], mild: ['Mild','\u{1F7E1}'], moderate: ['Moderate','\u{1F7E0}'], severe: ['Severe','\u{1F534}'] }; return map[level] || [level, '\u26AA']; }

/* ═══════════════════════════════════════════════════════════════════
   FaceMeshViewer \u2014 FLAME Template Edition
   Procedural 3D face + face crop texture + blendshape deformation
   + head pose + full telemetry HUD
   ═══════════════════════════════════════════════════════════════════ */
function FaceMeshViewer({ unifiedId, thingName }) {
  const [viewMode, setViewMode] = useState('reference');
  const [autoRotate, setAutoRotate] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showGazeRay, setShowGazeRay] = useState(true);
  const [showHeadPose, setShowHeadPose] = useState(true);
  const [showWireframe, setShowWireframe] = useState(false);
  const [hudPanel, setHudPanel] = useState('expression');

  const [meshData, setMeshData] = useState(null);
  const [activeBin, setActiveBin] = useState(null);
  const [timelineFrames, setTimelineFrames] = useState([]);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const playIntervalRef = useRef(null);
  const [faceCropUrls, setFaceCropUrls] = useState({});
  const [reconstructionStatus, setReconstructionStatus] = useState('none');
  const [reconstructing, setReconstructing] = useState(false);
  const glbMeshRef = useRef(null);
  const gltfLoaderRef = useRef(null);

  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const animFrameRef = useRef(null);
  const meshGroupRef = useRef(null);
  const faceMeshRef = useRef(null);
  const wireOverlayRef = useRef(null);
  const gazeLineRef = useRef(null);
  const headAxesRef = useRef(null);
  const texLoaderRef = useRef(null);
  const texCacheRef = useRef({});
  const faceGeoRef = useRef(null);
  const faceMaterialRef = useRef(null);

  const fetchMesh = useCallback(async () => {
    if (!unifiedId) return;
    setLoading(true); setError(null);
    try {
      const res = await ggosDeviceService.getIdentityMesh(unifiedId);
      if (res.success && res.data) {
        setMeshData(res.data);
        setReconstructionStatus(res.data.reconstruction_status || 'none');

        // If GLB mesh available, load it
        if (res.data.reconstruction_glb_url && res.data.reconstruction_status === 'ready') {
          loadGlb(res.data.reconstruction_glb_url);
        }

        const bins = Object.keys(res.data.reference_meshes || {});
        const sorted = YAW_ORDER.filter(k => bins.includes(k));
        setActiveBin(sorted.includes('yaw_0') ? 'yaw_0' : sorted[0] || null);
      } else { setMeshData(null); setActiveBin(null); }
    } catch (err) { setError(err.response?.data?.error || err.message || 'Failed to load mesh'); setMeshData(null);
    } finally { setLoading(false); }
  }, [unifiedId]);

  const fetchTimeline = useCallback(async () => {
    if (!thingName) return;
    setLoading(true); setError(null);
    try {
      const res = await ggosDeviceService.getSnapshots(thingName, { limit: MAX_TIMELINE_FRAMES, offset: 0, faces_only: true });
      if (!res.success) throw new Error('Failed to fetch snapshots');
      const snapshots = Array.isArray(res.data) ? res.data : (res.data?.snapshots || []);
      const frames = [];
      for (const snap of snapshots) {
        const sd = snap.snapshot_data || snap.snapshotData;
        if (!sd?.faces) continue;
        for (const face of sd.faces) {
          frames.push({
            capturedAt: snap.captured_at || snap.capturedAt,
            tickId: snap.tick_id || snap.tickId,
            landmarks_b64: face.landmarks_478?.landmarks_b64 || null,
            landmarkConfidence: face.landmarks_478?.confidence,
            hasIris: face.landmarks_478?.has_iris,
            faceIndex: face.face_index ?? 0,
            headPose: face.head_pose || null,
            blendshapes: face.blendshapes_52 || null,
            expression: face.mood?.primary_expression || '\u2014',
            secondaryExpression: face.mood?.secondary_expression,
            expressionIntensity: face.mood?.expression_intensity,
            expressionConfidence: face.mood?.confidence,
            moodSource: face.mood?.mood_source,
            eyeOpenness: face.mood?.eye_openness,
            blinkRate: face.mood?.blink_rate,
            adRelevance: face.mood?.ad_relevance,
            microExpressions: face.micro_expressions || null,
            drowsiness: face.drowsiness || null,
            gaze: face.gaze || null,
            attention: face.attention || null,
            actionUnits: face.action_units || null,
            demographics: face.demographics || null,
            identity: face.identity || null,
            faceCropS3Key: face.face_crop_s3_key || null,
            embeddingQuality: face.embedding?.quality_score,
          });
        }
      }
      frames.sort((a, b) => new Date(a.capturedAt) - new Date(b.capturedAt));
      setTimelineFrames(frames);
      setCurrentFrame(0);
      setIsPlaying(false);
      // Batch-sign face crop URLs
      const cropKeys = [...new Set(frames.map(f => f.faceCropS3Key).filter(Boolean))];
      if (cropKeys.length > 0) {
        try {
          const allUrls = {};
          for (let i = 0; i < cropKeys.length; i += 50) {
            const batch = cropKeys.slice(i, i + 50);
            const signRes = await ggosDeviceService.batchSignFaceCrops(batch);
            if (signRes.success) Object.assign(allUrls, signRes.urls);
          }
          setFaceCropUrls(allUrls);
        } catch (e) { /* face crops optional */ }
      }
    } catch (err) { setError(err.response?.data?.error || err.message || 'Failed to load timeline'); setTimelineFrames([]);
    } finally { setLoading(false); }
  }, [thingName]);

  useEffect(() => { if (viewMode === 'reference') fetchMesh(); else fetchTimeline(); }, [viewMode, fetchMesh, fetchTimeline]);

  /* ── GLB loading (MICA reconstruction) ─────────────────────── */
  const loadGlb = useCallback((url) => {
    if (!url) return;
    const loader = gltfLoaderRef.current || new GLTFLoader();
    loader.setCrossOrigin('anonymous');
    gltfLoaderRef.current = loader;

    loader.load(url, (gltf) => {
      const group = meshGroupRef.current;
      if (!group) return;

      // Remove previous GLB mesh if any
      if (glbMeshRef.current) {
        group.remove(glbMeshRef.current);
        glbMeshRef.current.traverse(obj => {
          if (obj.geometry) obj.geometry.dispose();
          if (obj.material) {
            if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose());
            else obj.material.dispose();
          }
        });
      }

      // Hide procedural face when GLB is loaded
      if (faceMeshRef.current) faceMeshRef.current.visible = false;
      if (wireOverlayRef.current) wireOverlayRef.current.visible = false;

      // Add GLB scene — keep original textures, enhance materials for lighting
      const glbScene = gltf.scene;

      glbScene.traverse(obj => {
        if (obj.isMesh && obj.geometry) {
          obj.geometry.computeVertexNormals();
          obj.castShadow = true;
          obj.receiveShadow = true;

          // Upgrade to MeshPhysicalMaterial while preserving the original texture
          const oldMat = obj.material;
          const existingMap = oldMat?.map || null;

          // Keep the texture — it's the reconstructed face projection
          const newMat = new THREE.MeshPhysicalMaterial({
            map: existingMap,
            color: existingMap ? 0xffffff : 0xdba27a,
            roughness: 0.5,
            metalness: 0.0,
            clearcoat: 0.04,
            clearcoatRoughness: 0.45,
            sheen: 0.15,
            sheenRoughness: 0.6,
            sheenColor: new THREE.Color(0xffb090),
            side: THREE.DoubleSide,
          });

          // Dispose old material but NOT its texture (we transferred it)
          if (oldMat) {
            oldMat.map = null;
            oldMat.dispose();
          }

          obj.material = newMat;
        }
      });

      // Center + normalise into the viewer
      const box = new THREE.Box3().setFromObject(glbScene);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const targetSize = 0.30;
      const s = targetSize / maxDim;

      // Wrap in pivot: inner group offsets to origin, outer scales
      const pivot = new THREE.Group();
      glbScene.position.set(-center.x, -center.y, -center.z);
      pivot.add(glbScene);
      pivot.scale.setScalar(s);

      group.add(pivot);
      glbMeshRef.current = pivot;
    }, undefined, (err) => {
      console.warn('GLB load failed:', err);
      setError('Failed to load 3D mesh — check browser console');
      // Show procedural face as fallback
      if (faceMeshRef.current) faceMeshRef.current.visible = true;
    });
  }, []);

  const triggerReconstruct = useCallback(async () => {
    if (!unifiedId || reconstructing) return;
    setReconstructing(true);
    try {
      await ggosDeviceService.triggerReconstruct(unifiedId);
      setReconstructionStatus('pending');
      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const res = await ggosDeviceService.getIdentityMesh(unifiedId);
          if (res.success && res.data?.reconstruction_status === 'ready') {
            clearInterval(poll);
            setReconstructionStatus('ready');
            setReconstructing(false);
            if (res.data.reconstruction_glb_url) loadGlb(res.data.reconstruction_glb_url);
          } else if (res.data?.reconstruction_status === 'failed') {
            clearInterval(poll);
            setReconstructionStatus('failed');
            setReconstructing(false);
          }
        } catch { /* continue polling */ }
      }, 3000);
      // Stop polling after 2 minutes
      setTimeout(() => { clearInterval(poll); setReconstructing(false); }, 120000);
    } catch (err) {
      console.error('Reconstruct failed:', err);
      setReconstructing(false);
    }
  }, [unifiedId, reconstructing, loadGlb]);

  /* ── Three.js scene ────────────────────────────────────────── */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const w = container.clientWidth, h = container.clientHeight;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0f0f1e);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(35, w / h, 0.01, 100);
    camera.position.set(0, 0.02, 0.55);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.1;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true; controls.dampingFactor = 0.08;
    controls.autoRotate = true; controls.autoRotateSpeed = 0.8;
    controls.minDistance = 0.15; controls.maxDistance = 3;
    controls.target.set(0, 0, 0);
    controlsRef.current = controls;

    scene.add(new THREE.AmbientLight(0xfff5ee, 0.35));
    // Portrait 3-point lighting: strong key to sculpt contours
    const kl = new THREE.DirectionalLight(0xffffff, 1.4); kl.position.set(1, 1.5, 2); scene.add(kl);
    const fl = new THREE.DirectionalLight(0xc8d8ff, 0.45); fl.position.set(-2, 0.5, 1); scene.add(fl);
    const rl = new THREE.DirectionalLight(0xffd4a0, 0.55); rl.position.set(-0.5, 0.2, -2); scene.add(rl);   // rim/back
    const tl = new THREE.DirectionalLight(0xffe8d0, 0.4); tl.position.set(0, 3, 0.5); scene.add(tl);        // top/hair

    const ground = new THREE.Mesh(
      new THREE.CircleGeometry(0.4, 64),
      new THREE.MeshStandardMaterial({ color: 0x1a1a2e, roughness: 0.9 })
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.2;
    scene.add(ground);

    const group = new THREE.Group();
    scene.add(group);
    meshGroupRef.current = group;
    texLoaderRef.current = new THREE.TextureLoader();

    const faceGeo = createFaceGeometry();
    const baseArr = new Float32Array(faceGeo.attributes.position.count * 3);
    for (let i = 0; i < faceGeo.attributes.position.count; i++) {
      baseArr[i*3]   = faceGeo.attributes.position.getX(i);
      baseArr[i*3+1] = faceGeo.attributes.position.getY(i);
      baseArr[i*3+2] = faceGeo.attributes.position.getZ(i);
    }
    faceGeo.userData.basePositions = baseArr;
    faceGeoRef.current = faceGeo;

    const faceMat = new THREE.MeshStandardMaterial({
      color: 0xd4a574, roughness: 0.65, metalness: 0.05, side: THREE.FrontSide
    });
    faceMaterialRef.current = faceMat;

    const faceMesh = new THREE.Mesh(faceGeo, faceMat);
    group.add(faceMesh);
    faceMeshRef.current = faceMesh;

    const wireGeo = faceGeo.clone();
    const wireMesh = new THREE.Mesh(wireGeo, new THREE.MeshBasicMaterial({
      color: 0x6366f1, wireframe: true, transparent: true, opacity: 0.08
    }));
    wireMesh.visible = false;
    group.add(wireMesh);
    wireOverlayRef.current = wireMesh;

    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const ro = new ResizeObserver(([entry]) => {
      const { width: rw, height: rh } = entry.contentRect;
      if (!rw || !rh) return;
      camera.aspect = rw / rh;
      camera.updateProjectionMatrix();
      renderer.setSize(rw, rh);
    });
    ro.observe(container);

    return () => {
      ro.disconnect();
      cancelAnimationFrame(animFrameRef.current);
      controls.dispose();
      renderer.dispose();
      if (renderer.domElement.parentElement) renderer.domElement.parentElement.removeChild(renderer.domElement);
      scene.traverse(obj => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose());
          else obj.material.dispose();
        }
      });
    };
  }, []);

  useEffect(() => { if (controlsRef.current) controlsRef.current.autoRotate = autoRotate; }, [autoRotate]);
  useEffect(() => { if (wireOverlayRef.current) wireOverlayRef.current.visible = showWireframe; }, [showWireframe]);

  const loadFaceCropTexture = useCallback((s3Key) => {
    if (!s3Key || !texLoaderRef.current) return;
    const url = faceCropUrls[s3Key];
    if (!url) return;
    if (texCacheRef.current[s3Key]) {
      const mat = faceMaterialRef.current;
      if (mat) { mat.map = texCacheRef.current[s3Key]; mat.color.set(0xffffff); mat.needsUpdate = true; }
      return;
    }
    texLoaderRef.current.load(url, (texture) => {
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.minFilter = THREE.LinearMipMapLinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texCacheRef.current[s3Key] = texture;
      const mat = faceMaterialRef.current;
      if (mat) { mat.map = texture; mat.color.set(0xffffff); mat.needsUpdate = true; }
    });
  }, [faceCropUrls]);

  const applyFrame = useCallback((frameData) => {
    const group = meshGroupRef.current;
    const faceMesh = faceMeshRef.current;
    const faceGeo = faceGeoRef.current;
    if (!group || !faceMesh || !faceGeo) return;

    applyProcBlendshapes(faceGeo, frameData?.blendshapes);

    if (frameData?.headPose) {
      const { yaw, pitch, roll } = frameData.headPose;
      faceMesh.rotation.set(
        (pitch || 0) * Math.PI / 180,
        -(yaw || 0) * Math.PI / 180,
        -(roll || 0) * Math.PI / 180,
        'YXZ'
      );
      if (wireOverlayRef.current) wireOverlayRef.current.rotation.copy(faceMesh.rotation);
    } else {
      faceMesh.rotation.set(0, 0, 0);
      if (wireOverlayRef.current) wireOverlayRef.current.rotation.set(0, 0, 0);
    }

    if (frameData?.faceCropS3Key) loadFaceCropTexture(frameData.faceCropS3Key);

    // Gaze ray
    if (gazeLineRef.current) {
      group.remove(gazeLineRef.current);
      gazeLineRef.current.geometry.dispose();
      gazeLineRef.current.material.dispose();
      gazeLineRef.current = null;
    }
    if (showGazeRay && frameData?.gaze && (frameData.gaze.gaze_yaw != null || frameData.gaze.gaze_pitch != null)) {
      const yR = (frameData.gaze.gaze_yaw || 0) * Math.PI / 180;
      const pR = (frameData.gaze.gaze_pitch || 0) * Math.PI / 180;
      const len = 0.25;
      const gg = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(0, 0.02, 0.06),
        new THREE.Vector3(Math.sin(yR) * len, 0.02 + Math.sin(pR) * len, 0.06 + Math.cos(yR) * Math.cos(pR) * len)
      ]);
      const gl = new THREE.Line(gg, new THREE.LineBasicMaterial({ color: 0x06b6d4, linewidth: 2, transparent: true, opacity: 0.85 }));
      group.add(gl);
      gazeLineRef.current = gl;
    }

    // Head axes
    if (headAxesRef.current) {
      for (const a of headAxesRef.current) { group.remove(a); a.geometry.dispose(); a.material.dispose(); }
      headAxesRef.current = null;
    }
    if (showHeadPose && frameData?.headPose) {
      const { yaw, pitch, roll } = frameData.headPose;
      if (yaw != null || pitch != null || roll != null) {
        const quat = new THREE.Quaternion().setFromEuler(
          new THREE.Euler((pitch||0)*Math.PI/180, -(yaw||0)*Math.PI/180, -(roll||0)*Math.PI/180, 'YXZ')
        );
        const axes = [];
        for (const { dir, color } of [
          { dir: new THREE.Vector3(1,0,0), color: 0xff4444 },
          { dir: new THREE.Vector3(0,1,0), color: 0x44ff44 },
          { dir: new THREE.Vector3(0,0,1), color: 0x4488ff }
        ]) {
          const d = dir.clone().applyQuaternion(quat).multiplyScalar(0.1);
          const geo = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0,0,0), new THREE.Vector3(d.x,d.y,d.z)]);
          const line = new THREE.Line(geo, new THREE.LineBasicMaterial({ color }));
          group.add(line);
          axes.push(line);
        }
        headAxesRef.current = axes;
      }
    }
  }, [showGazeRay, showHeadPose, loadFaceCropTexture]);

  useEffect(() => {
    if (viewMode !== 'reference') return;
    applyFrame(null);
    const mat = faceMaterialRef.current;
    if (mat) { mat.map = null; mat.color.set(0xd4a574); mat.needsUpdate = true; }
  }, [viewMode, meshData, activeBin, applyFrame]);

  useEffect(() => {
    if (viewMode !== 'timeline' || !timelineFrames.length) return;
    const frame = timelineFrames[currentFrame];
    if (frame) applyFrame(frame);
  }, [viewMode, timelineFrames, currentFrame, applyFrame]);

  useEffect(() => {
    if (playIntervalRef.current) clearInterval(playIntervalRef.current);
    if (!isPlaying || timelineFrames.length < 2) return;
    const ms = Math.max(50, Math.round(200 / playbackSpeed));
    playIntervalRef.current = setInterval(() => {
      setCurrentFrame(f => {
        if (f >= timelineFrames.length - 1) { setIsPlaying(false); return f; }
        return f + 1;
      });
    }, ms);
    return () => clearInterval(playIntervalRef.current);
  }, [isPlaying, playbackSpeed, timelineFrames.length]);

  const resetCamera = useCallback(() => {
    if (!cameraRef.current || !controlsRef.current) return;
    cameraRef.current.position.set(0, 0.02, 0.55);
    controlsRef.current.target.set(0, 0, 0);
    controlsRef.current.update();
  }, []);

  const bins = meshData?.reference_meshes ? Object.keys(meshData.reference_meshes) : [];
  const sortedBins = YAW_ORDER.filter(k => bins.includes(k));
  const currentBinData = (meshData?.reference_meshes && activeBin) ? meshData.reference_meshes[activeBin] : null;
  const quality = currentBinData?.quality;
  const isEmpty = !loading && !error && viewMode === 'reference' && (!meshData || !bins.length);
  const timelineEmpty = !loading && !error && viewMode === 'timeline' && !timelineFrames.length;
  const f = timelineFrames[currentFrame] || null;
  const hasTimeline = !!thingName;
  const hasFaceCrop = f?.faceCropS3Key && faceCropUrls[f.faceCropS3Key];

  const activeBlendshapes = useMemo(() => {
    if (!f?.blendshapes) return [];
    return KEY_BLENDSHAPES.map(kb => ({ ...kb, value: f.blendshapes[kb.key] ?? 0 })).filter(b => b.value > 0.01).sort((a, b) => b.value - a.value);
  }, [f]);
  const activeAUs = useMemo(() => {
    if (!f?.actionUnits) return [];
    return Object.entries(f.actionUnits).filter(([, v]) => typeof v === 'number' && v > 0.05).sort((a, b) => b[1] - a[1]);
  }, [f]);

  return (
    <div className="fmv">
      {hasTimeline && (
        <div className={`fmv__mode-tabs${viewMode === 'timeline' ? ' fmv__mode-tabs--hud' : ''}`}>
          <button className={`fmv__mode-tab${viewMode === 'reference' ? ' fmv__mode-tab--active' : ''}`} onClick={() => { setViewMode('reference'); setIsPlaying(false); }}>Reference</button>
          <button className={`fmv__mode-tab${viewMode === 'timeline' ? ' fmv__mode-tab--active' : ''}`} onClick={() => { setViewMode('timeline'); setAutoRotate(false); }}>Timeline \u25B6</button>
        </div>
      )}

      <div ref={containerRef} className="fmv__canvas" />

      {loading && (<div className="fmv__overlay"><div className="fmv__spinner" /><span className="fmv__overlay-text">{viewMode === 'timeline' ? 'Loading timeline\u2026' : 'Loading mesh\u2026'}</span></div>)}
      {!loading && error && (<div className="fmv__overlay"><span className="fmv__overlay-error">{error}</span><button className="fmv__retry-btn" onClick={viewMode === 'reference' ? fetchMesh : fetchTimeline}>Retry</button></div>)}
      {isEmpty && (<div className="fmv__overlay"><div className="fmv__empty-icon"><svg viewBox="0 0 24 24"><circle cx="12" cy="8" r="5"/><path d="M3 21c0-4.97 4.03-9 9-9s9 4.03 9 9"/></svg></div><span className="fmv__overlay-text">No face mesh data</span></div>)}
      {timelineEmpty && (<div className="fmv__overlay"><div className="fmv__empty-icon"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></div><span className="fmv__overlay-text">No timeline frames</span></div>)}

      {viewMode === 'timeline' && f && <div className="fmv__texture-badge">{hasFaceCrop ? '\u{1F4F8} Textured' : '\u{1F3A8} Skin Tone'}</div>}

      {viewMode === 'reference' && sortedBins.length > 0 && (
        <div className="fmv__yaw-bar">
          {sortedBins.map(bin => { const d = meshData.reference_meshes[bin]; return (
            <button key={bin} className={`fmv__yaw-tab${activeBin === bin ? ' fmv__yaw-tab--active' : ''}`} onClick={() => setActiveBin(bin)}>
              {YAW_LABELS[bin] || bin}{d?.quality != null && <span className="fmv__yaw-quality">{(d.quality * 100).toFixed(0)}%</span>}
            </button>); })}
        </div>
      )}

      {viewMode === 'timeline' && timelineFrames.length > 0 && (
        <div className="fmv__timeline">
          <div className="fmv__timeline-controls">
            <button className="fmv__tl-btn" onClick={() => { setCurrentFrame(0); setIsPlaying(false); }}>{'\u23EE'}</button>
            <button className="fmv__tl-btn" onClick={() => setCurrentFrame(p => Math.max(0, p - 1))} disabled={currentFrame <= 0}>{'\u25C0'}</button>
            <button className="fmv__tl-btn fmv__tl-btn--play" onClick={() => { if (currentFrame >= timelineFrames.length - 1) setCurrentFrame(0); setIsPlaying(p => !p); }}>{isPlaying ? '\u23F8' : '\u25B6'}</button>
            <button className="fmv__tl-btn" onClick={() => setCurrentFrame(p => Math.min(timelineFrames.length - 1, p + 1))} disabled={currentFrame >= timelineFrames.length - 1}>{'\u25B6'}</button>
            <button className="fmv__tl-btn" onClick={() => { setCurrentFrame(timelineFrames.length - 1); setIsPlaying(false); }}>{'\u23ED'}</button>
          </div>
          <div className="fmv__timeline-slider-row">
            <span className="fmv__tl-frame">{currentFrame + 1}/{timelineFrames.length}</span>
            <input type="range" className="fmv__tl-slider" min={0} max={timelineFrames.length - 1} value={currentFrame} onChange={e => { setCurrentFrame(Number(e.target.value)); setIsPlaying(false); }} />
            <span className="fmv__tl-time">{formatShortTime(f?.capturedAt)}</span>
          </div>
          <div className="fmv__timeline-options">
            <div className="fmv__speed-row">
              <span className="fmv__speed-label">Speed</span>
              {PLAYBACK_SPEEDS.map(s => (<button key={s} className={`fmv__speed-btn${playbackSpeed === s ? ' fmv__speed-btn--active' : ''}`} onClick={() => setPlaybackSpeed(s)}>{s}\u00D7</button>))}
            </div>
          </div>
        </div>
      )}

      <div className={`fmv__controls${viewMode === 'timeline' ? ' fmv__controls--hud' : ''}`}>
        <div className="fmv__controls-title">Render</div>
        <div className="fmv__control-row"><span className="fmv__control-label">Auto-rotate</span><button className={`fmv__toggle${autoRotate ? ' fmv__toggle--on' : ''}`} onClick={() => setAutoRotate(r => !r)} /></div>
        <div className="fmv__control-row"><span className="fmv__control-label">Wireframe</span><button className={`fmv__toggle${showWireframe ? ' fmv__toggle--on' : ''}`} onClick={() => setShowWireframe(v => !v)} /></div>
        {viewMode === 'timeline' && (<>
          <div className="fmv__control-row"><span className="fmv__control-label">Gaze ray</span><button className={`fmv__toggle${showGazeRay ? ' fmv__toggle--on' : ''}`} onClick={() => setShowGazeRay(v => !v)} /></div>
          <div className="fmv__control-row"><span className="fmv__control-label">Head axes</span><button className={`fmv__toggle${showHeadPose ? ' fmv__toggle--on' : ''}`} onClick={() => setShowHeadPose(v => !v)} /></div>
        </>)}
        <button className="fmv__reset-btn" onClick={resetCamera}>Reset Camera</button>
        {viewMode === 'reference' && reconstructionStatus !== 'ready' && (
          <button
            className={`fmv__reconstruct-btn${reconstructing ? ' fmv__reconstruct-btn--loading' : ''}`}
            onClick={triggerReconstruct}
            disabled={reconstructing}
          >
            {reconstructing ? 'Reconstructing\u2026' : reconstructionStatus === 'pending' ? 'Pending\u2026' : '\u{1F3AD} Reconstruct 3D'}
          </button>
        )}
        {viewMode === 'reference' && reconstructionStatus === 'ready' && (
          <>
            <div className="fmv__reconstruct-badge">{'\u2705'} MICA 3D Mesh</div>
            <button
              className={`fmv__reconstruct-btn fmv__reconstruct-btn--refresh${reconstructing ? ' fmv__reconstruct-btn--loading' : ''}`}
              onClick={triggerReconstruct}
              disabled={reconstructing}
            >
              {reconstructing ? 'Reconstructing\u2026' : '\u{1F504} Re-reconstruct'}
            </button>
          </>
        )}
      </div>

      {viewMode === 'reference' && currentBinData && (
        <div className="fmv__info">
          <div className="fmv__info-row"><span className="fmv__info-label">ID</span><span className="fmv__info-value" title={meshData.unified_id}>{meshData.unified_id ? meshData.unified_id.slice(0, 12) + '\u2026' : '\u2014'}</span></div>
          <div className="fmv__info-row"><span className="fmv__info-label">Yaw</span><span className="fmv__info-value">{YAW_LABELS[activeBin] || activeBin}</span></div>
          <div className="fmv__info-row"><span className="fmv__info-label">Quality</span><span className={`fmv__quality-badge fmv__quality-badge--${qualityTier(quality)}`}>{quality != null ? `${(quality * 100).toFixed(1)}%` : '\u2014'}</span></div>
          <div className="fmv__info-row"><span className="fmv__info-label">Captured</span><span className="fmv__info-value">{formatTimestamp(currentBinData.captured_at)}</span></div>
        </div>
      )}

      {viewMode === 'timeline' && f && (
        <div className="fmv__hud">
          <div className="fmv__hud-tabs">
            {[{ id: 'expression', label: '\u{1F60A} Expr' },{ id: 'micro', label: '\u{1F52C} Micro' },{ id: 'gaze', label: '\u{1F441} Gaze' },{ id: 'drowsiness', label: '\u{1F634} Alert' },{ id: 'identity', label: '\u{1F194} ID' }].map(tab => (
              <button key={tab.id} className={`fmv__hud-tab${hudPanel === tab.id ? ' fmv__hud-tab--active' : ''}`} onClick={() => setHudPanel(tab.id)}>{tab.label}</button>
            ))}
          </div>

          {hudPanel === 'expression' && (
            <div className="fmv__hud-body">
              <div className="fmv__hud-hero">
                <span className="fmv__hud-expression">{f.expression}</span>
                {f.secondaryExpression && <span className="fmv__hud-secondary">{f.secondaryExpression}</span>}
                {f.expressionIntensity != null && <span className="fmv__hud-intensity">{(f.expressionIntensity * 100).toFixed(0)}%</span>}
              </div>
              {activeBlendshapes.length > 0 && (<div className="fmv__bs-panel"><div className="fmv__bs-title">Blendshapes ({activeBlendshapes.length})</div>
                {activeBlendshapes.slice(0, 12).map(b => (<div key={b.key} className="fmv__bs-row"><span className="fmv__bs-emoji">{b.emoji}</span><span className="fmv__bs-label">{b.label}</span><div className="fmv__bs-bar-bg"><div className="fmv__bs-bar-fill" style={{ width: `${b.value * 100}%`, background: b.value > 0.7 ? '#f43f5e' : b.value > 0.4 ? '#f59e0b' : '#6366f1' }} /></div><span className="fmv__bs-val">{(b.value * 100).toFixed(0)}</span></div>))}
              </div>)}
              {activeAUs.length > 0 && (<div className="fmv__au-panel"><div className="fmv__bs-title">FACS Action Units</div>
                {activeAUs.slice(0, 8).map(([name, val]) => (<div key={name} className="fmv__bs-row"><span className="fmv__bs-label fmv__au-name">{name.replace(/_/g, ' ')}</span><div className="fmv__bs-bar-bg"><div className="fmv__bs-bar-fill fmv__au-fill" style={{ width: `${val * 100}%` }} /></div><span className="fmv__bs-val">{(val * 100).toFixed(0)}</span></div>))}
              </div>)}
            </div>
          )}

          {hudPanel === 'micro' && (
            <div className="fmv__hud-body">
              {f.microExpressions ? (<div className="fmv__micro-grid">
                {[{ key: 'smile_intensity', label: 'Smile', emoji: '\u{1F60A}', color: '#22c55e' },{ key: 'frown_intensity', label: 'Frown', emoji: '\u2639\uFE0F', color: '#ef4444' },{ key: 'surprise_level', label: 'Surprise', emoji: '\u{1F632}', color: '#f59e0b' },{ key: 'disgust_level', label: 'Disgust', emoji: '\u{1F922}', color: '#84cc16' },{ key: 'squint_level', label: 'Squint', emoji: '\u{1F611}', color: '#8b5cf6' },{ key: 'jaw_tension', label: 'Jaw Tension', emoji: '\u{1F62C}', color: '#ec4899' },{ key: 'nose_wrinkle', label: 'Nose Wrinkle', emoji: '\u{1F928}', color: '#14b8a6' }].map(item => {
                  const val = f.microExpressions[item.key]; if (val == null) return null;
                  return (<div key={item.key} className="fmv__micro-item"><span className="fmv__micro-emoji">{item.emoji}</span><span className="fmv__micro-label">{item.label}</span><div className="fmv__micro-bar-bg"><div className="fmv__micro-bar-fill" style={{ width: `${val * 100}%`, background: item.color }} /></div><span className="fmv__micro-val">{(val * 100).toFixed(0)}%</span></div>);
                })}
              </div>) : <div className="fmv__hud-empty">No micro-expression data</div>}
            </div>
          )}

          {hudPanel === 'gaze' && (
            <div className="fmv__hud-body">
              {f.headPose && (<div className="fmv__pose-section"><div className="fmv__bs-title">Head Pose</div><div className="fmv__pose-grid">
                {[{ label: 'Yaw', value: f.headPose.yaw, color: '#ef4444' },{ label: 'Pitch', value: f.headPose.pitch, color: '#22c55e' },{ label: 'Roll', value: f.headPose.roll, color: '#3b82f6' }].map(p => (
                  <div key={p.label} className="fmv__pose-item"><span className="fmv__pose-label" style={{ color: p.color }}>{p.label}</span><span className="fmv__pose-value">{p.value != null ? `${p.value.toFixed(1)}\u00B0` : '\u2014'}</span></div>
                ))}</div></div>)}
              {f.gaze ? (<div className="fmv__gaze-section"><div className="fmv__bs-title">Gaze</div><div className="fmv__gaze-grid">
                {f.gaze.gaze_yaw != null && <div className="fmv__gaze-stat"><span className="fmv__gaze-stat-label">Yaw</span><span className="fmv__gaze-stat-val">{f.gaze.gaze_yaw.toFixed(1)}\u00B0</span></div>}
                {f.gaze.gaze_pitch != null && <div className="fmv__gaze-stat"><span className="fmv__gaze-stat-label">Pitch</span><span className="fmv__gaze-stat-val">{f.gaze.gaze_pitch.toFixed(1)}\u00B0</span></div>}
                {f.gaze.screen_zone != null && <div className="fmv__gaze-stat"><span className="fmv__gaze-stat-label">Zone</span><span className="fmv__gaze-stat-val fmv__gaze-zone">Z{f.gaze.screen_zone}</span></div>}
                {f.gaze.dwell_ms != null && <div className="fmv__gaze-stat"><span className="fmv__gaze-stat-label">Dwell</span><span className="fmv__gaze-stat-val">{f.gaze.dwell_ms}ms</span></div>}
              </div>
              {f.gaze.is_verified_view && <div className="fmv__verified-badge">{'\u2705'} Verified View</div>}
              {(f.gaze.icr_x != null || f.gaze.icr_y != null) && (<div className="fmv__iris-section"><div className="fmv__bs-title">Iris Center</div><div className="fmv__iris-crosshair"><svg viewBox="0 0 100 100" className="fmv__iris-svg"><rect x="0" y="0" width="100" height="100" rx="8" fill="#0f0f1e" stroke="#1e293b" strokeWidth="1" /><line x1="50" y1="0" x2="50" y2="100" stroke="#1e293b" strokeWidth="0.5" /><line x1="0" y1="50" x2="100" y2="50" stroke="#1e293b" strokeWidth="0.5" /><circle cx={(f.gaze.icr_x ?? 0.5) * 100} cy={(f.gaze.icr_y ?? 0.5) * 100} r="6" fill="#06b6d4" opacity="0.8" /><circle cx={(f.gaze.icr_x ?? 0.5) * 100} cy={(f.gaze.icr_y ?? 0.5) * 100} r="3" fill="#fff" /></svg></div></div>)}
              </div>) : <div className="fmv__hud-empty">No gaze data</div>}
              {f.attention && (<div className="fmv__attention-section"><div className="fmv__bs-title">Attention</div><div className="fmv__attention-score">{f.attention.score != null ? `${(f.attention.score * 100).toFixed(0)}%` : '\u2014'}</div>
                {f.attention.components && Object.entries(f.attention.components).map(([k, v]) => (<div key={k} className="fmv__bs-row"><span className="fmv__bs-label">{k}</span><div className="fmv__bs-bar-bg"><div className="fmv__bs-bar-fill" style={{ width: `${(typeof v === 'number' ? v : 0) * 100}%`, background: '#6366f1' }} /></div><span className="fmv__bs-val">{typeof v === 'number' ? (v * 100).toFixed(0) : '\u2014'}</span></div>))}
              </div>)}
            </div>
          )}

          {hudPanel === 'drowsiness' && (
            <div className="fmv__hud-body">
              {f.drowsiness ? (<div className="fmv__drowsy-panel">
                {f.drowsiness.drowsiness_level && (() => { const [label, dot] = drowsinessLabel(f.drowsiness.drowsiness_level); return <div className="fmv__drowsy-hero"><span className="fmv__drowsy-dot">{dot}</span><span className="fmv__drowsy-level">{label}</span></div>; })()}
                <div className="fmv__drowsy-grid">
                  {f.drowsiness.fatigue_score != null && (<div className="fmv__drowsy-metric"><span className="fmv__drowsy-metric-label">Fatigue</span><div className="fmv__drowsy-gauge"><div className="fmv__drowsy-gauge-fill" style={{ width: `${f.drowsiness.fatigue_score * 100}%`, background: f.drowsiness.fatigue_score > 0.6 ? '#ef4444' : f.drowsiness.fatigue_score > 0.3 ? '#f59e0b' : '#22c55e' }} /></div><span className="fmv__drowsy-metric-val">{(f.drowsiness.fatigue_score * 100).toFixed(0)}%</span></div>)}
                  {f.drowsiness.perclos_score != null && (<div className="fmv__drowsy-metric"><span className="fmv__drowsy-metric-label">PERCLOS</span><div className="fmv__drowsy-gauge"><div className="fmv__drowsy-gauge-fill" style={{ width: `${f.drowsiness.perclos_score * 100}%`, background: '#8b5cf6' }} /></div><span className="fmv__drowsy-metric-val">{(f.drowsiness.perclos_score * 100).toFixed(0)}%</span></div>)}
                  {f.drowsiness.yawn_detected != null && <div className="fmv__micro-stat"><span>{'\u{1F971}'} Yawn</span><strong>{f.drowsiness.yawn_detected ? 'YES' : 'No'}</strong></div>}
                </div>
              </div>) : <div className="fmv__hud-empty">No drowsiness data</div>}
            </div>
          )}

          {hudPanel === 'identity' && (
            <div className="fmv__hud-body"><div className="fmv__id-panel">
              {f.demographics && (<div className="fmv__id-section"><div className="fmv__bs-title">Demographics</div><div className="fmv__id-demo-row">
                {f.demographics.gender && <span className="fmv__id-tag">{f.demographics.gender === 'male' ? '\u2642' : '\u2640'} {f.demographics.gender}</span>}
                {f.demographics.estimated_age != null && <span className="fmv__id-tag">{'\u{1F382}'} {f.demographics.estimated_age}y</span>}
                {f.demographics.is_minor && <span className="fmv__id-tag fmv__id-tag--warn">{'\u26A0'} Minor</span>}
              </div></div>)}
              {f.identity && (<div className="fmv__id-section"><div className="fmv__bs-title">Session</div><div className="fmv__id-grid">
                {f.identity.unified_id && <div className="fmv__id-kv"><span>Unified ID</span><strong title={f.identity.unified_id}>{f.identity.unified_id.slice(0, 8)}\u2026</strong></div>}
                {f.identity.match_type && <div className="fmv__id-kv"><span>Match</span><strong>{f.identity.match_type}</strong></div>}
                {f.identity.similarity != null && <div className="fmv__id-kv"><span>Similarity</span><strong>{(f.identity.similarity * 100).toFixed(1)}%</strong></div>}
                {f.identity.sightings != null && <div className="fmv__id-kv"><span>Sightings</span><strong>{f.identity.sightings}</strong></div>}
              </div></div>)}
              <div className="fmv__id-section"><div className="fmv__bs-title">Signals</div><div className="fmv__id-grid">
                {f.embeddingQuality != null && <div className="fmv__id-kv"><span>Embedding Q</span><strong>{(f.embeddingQuality * 100).toFixed(0)}%</strong></div>}
                {f.adRelevance != null && <div className="fmv__id-kv"><span>Ad Relevance</span><strong>{(f.adRelevance * 100).toFixed(0)}%</strong></div>}
                {f.landmarkConfidence != null && <div className="fmv__id-kv"><span>Landmark Conf</span><strong>{(f.landmarkConfidence * 100).toFixed(0)}%</strong></div>}
                {f.hasIris != null && <div className="fmv__id-kv"><span>Iris</span><strong>{f.hasIris ? '\u2705' : '\u2014'}</strong></div>}
              </div></div>
            </div></div>
          )}
        </div>
      )}
    </div>
  );
}

FaceMeshViewer.propTypes = { unifiedId: PropTypes.string.isRequired, thingName: PropTypes.string };
FaceMeshViewer.defaultProps = { thingName: null };
export default FaceMeshViewer;
